import { AppContextProvider, KFNotificationProvider } from '@kf/ic2fx';
import { Box, Grid } from '@mui/material';
import { Ic2fxComponentType, NavigationPath } from 'models/enum';
import AuthProvider, {
  IAuthorizationProviderDispatchProps,
} from 'modules/auth/components/AuthProvider';
import B2CPopupEnd from 'modules/auth/components/b2c/B2CPopupEnd';
import B2CPopupStart from 'modules/auth/components/b2c/B2CPopupStart';
import PopupNotification from 'modules/popupNotification/components/PopupNotification';
import React, { ReactElement, Suspense, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import Ic2fxComponentWrapper from 'shared/components/ic2fxComponentWrapper';
import UnsupportedDevicePage from 'shared/components/unsupportedDevicePage';
import { isApplicationSupportedOnDevice } from 'shared/helperFunctions';
import store from 'store';
import ThemeHandler from 'styles/ThemeHandler';
import { checkForInitialWindowSize } from 'utilities/authorizationUtils';
import { ConfigContextProvider } from 'utilities/context/ConfigContext';
import useManifestVersion from 'utilities/hooks/useManifestVersion';
import useWindowSize from 'utilities/hooks/useWindowsSize';
import './App.scss';
import { checkAppIsRunningInTeams } from './utilities/teamsRuntime';

import { app } from '@microsoft/teams-js';
import { KF1AppRootProvider } from 'kfone-frontend-core';
import EEApp from './EEApp';
import EEAuthSteps from './EEAuthSteps';
import getLanguageResources from './shared/locales';
import AppToolbar from './shared/modules/appToolbar/appToolbar';

//Bot Login Pages
import BotAuthStart from 'modules/auth/components/BotAuthStart';
import BotTeamsAppAuthProvider from 'modules/auth/components/BotTeamsAppAuthProvider';

// Check if current path is bot auth start page
const isBotAuthStartPage = (): boolean => {
  const path = window.location.pathname.toLowerCase();
  return ['/botlogin', '/botstart'].includes(path);
};

// Render bot auth pages based on current path
export const BotTeamsAppAuthPages = (): ReactElement | null => {
  const path = window.location.pathname.toLowerCase();
  switch (path) {
    case '/botstart':
      return <BotAuthStart />;
    case '/botlogin':
      return <BotTeamsAppAuthProvider />;
    default:
      return null;
  }
};

export const EEAuthStepsPages = (): ReactElement => {
  return (
    <Routes>
      <Route path={NavigationPath.authstart} element={<B2CPopupStart />} />
      <Route path={NavigationPath.authend} element={<B2CPopupEnd />}></Route>
      <Route path='/botlogin' element={<BotTeamsAppAuthProvider />} />
    </Routes>
  );
};

interface IBasicProvider extends IAuthorizationProviderDispatchProps {
  children: ReactElement | ReactElement[];
  checkUnsupportedWindowSize: boolean;
}

const BasicProviders = ({ children, saveSSOToken, checkUnsupportedWindowSize }: IBasicProvider) => (
  <ThemeHandler>
    <Provider store={store}>
      <KFNotificationProvider
        maxSnack={5}
        preventDuplicate
        autoHideDuration={6000}
        content={(key, message) => <PopupNotification id={key} message={message} />}
      >
        <AuthProvider
          saveSSOToken={saveSSOToken}
          appIsOnTeams={checkAppIsRunningInTeams()}
          isWindowSizeUnsupported={checkUnsupportedWindowSize}
        >
          <ConfigContextProvider>
            {checkUnsupportedWindowSize ? (
              <Grid
                id='unSupported-window-size'
                sx={{ px: 2 }}
                container
                direction='column'
                alignContent='center'
                alignItems='center'
                rowSpacing={2}
              >
                <UnsupportedDevicePage />
              </Grid>
            ) : (
              children
            )}
          </ConfigContextProvider>
        </AuthProvider>
      </KFNotificationProvider>
    </Provider>
  </ThemeHandler>
);

const App = (): ReactElement => {
  useManifestVersion();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string>('');
  const [roles, setRoles] = useState<string[]>([]);
  const [unsupportedWindowSize, setUnsupportedWindowSize] = useState<boolean>(false);
  const windowSize: number[] = useWindowSize();

  useEffect(() => {
    if (!checkForInitialWindowSize(windowSize)) {
      const activePath = window.location.pathname;
      const isAppUnSupported: boolean =
        !isApplicationSupportedOnDevice(windowSize[0]) &&
        !(activePath === NavigationPath.authstart || activePath === NavigationPath.authend);

      setUnsupportedWindowSize(isAppUnSupported);
    }
  }, [windowSize]);

  const saveSSOToken = (userToken: string, userRoles: string[]) => {
    setToken(userToken);
    setRoles(userRoles);
    setIsAuthenticated(true);
  };

  // Handle bot auth start pages separately
  if (isBotAuthStartPage()) {
    return (
      <KF1AppRootProvider languageProps={getLanguageResources()}>
        <ThemeHandler>
          <Provider store={store}>
            <BotTeamsAppAuthPages />
          </Provider>
        </ThemeHandler>
      </KF1AppRootProvider>
    );
  }

  return (
    <BasicProviders saveSSOToken={saveSSOToken} checkUnsupportedWindowSize={unsupportedWindowSize}>
      <KF1AppRootProvider languageProps={getLanguageResources()}>
        <Suspense fallback={<Ic2fxComponentWrapper componentType={Ic2fxComponentType.loader} />}>
          {isAuthenticated ? (
            <>
              <EEApp token={token} roles={roles} />
            </>
          ) : (
            <EEAuthSteps />
          )}
        </Suspense>
      </KF1AppRootProvider>
    </BasicProviders>
  );
};

export default App;
